import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCategories } from "@/contexts/CategoriesContext";
import { useHousehold } from "@/contexts/HouseholdContext";
import { fmtCurrency, fmtDate, PAYMENT_METHODS } from "@/lib/format";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import {
    Receipt, Search, Filter, Plus, Pencil, Trash2, Download, LayoutGrid, Table as TableIcon,
    ArrowUpRight, ArrowDownRight, Users, User as UserIcon, ScanLine, Loader2, Lock,
} from "lucide-react";
import { hasProAccess } from "@/lib/plans";
import { scanReceiptImage } from "@/lib/receiptScan";
import ScanReviewDialog from "@/components/ScanReviewDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { iconForCategoryName } from "@/lib/categoryIcons";

const SORTS = [
    { value: "date_desc", label: "Newest first" },
    { value: "date_asc", label: "Oldest first" },
    { value: "amount_desc", label: "Amount: high to low" },
    { value: "amount_asc", label: "Amount: low to high" },
];

export default function Expenses() {
    const { user } = useAuth();
    const cur = user?.currency || "USD";
    const { categories, categoryNames } = useCategories();
    const { isMember, memberName } = useHousehold();


    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("table");
    const [search, setSearch] = useState("");
    const [type, setType] = useState("all");
    const [category, setCategory] = useState("all");
    const [sort, setSort] = useState("date_desc");
    // Opens with the user's preferred Combined/Just me default (Settings → Preferences).
    const [mine, setMine] = useState(() => user?.default_scope === "mine");
    const [editing, setEditing] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    // Receipt / screenshot scan (Pro/Family) — always goes through the review dialog here
    const navigate = useNavigate();
    const canScan = hasProAccess(user, isMember);
    const [scanning, setScanning] = useState(false);
    const [scanResult, setScanResult] = useState(null);
    const scanInputRef = useRef(null);
    const onScanClick = () => {
        if (!canScan) {
            toast("Receipt scan is a Pro feature", { action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
            return;
        }
        scanInputRef.current?.click();
    };
    const onScanFile = async (file) => {
        if (!file) return;
        setScanning(true);
        try {
            setScanResult(await scanReceiptImage(file));
        } catch (e) {
            toast.error(e.message);
        } finally {
            setScanning(false);
            if (scanInputRef.current) scanInputRef.current.value = "";
        }
    };

    const load = async () => {
        setLoading(true);
        const params = { sort };
        if (type !== "all") params.type = type;
        if (category !== "all") params.category = category;
        if (search) params.search = search;
        if (isMember && mine) params.mine = true;
        try {
            const { data } = await api.get("/expenses", { params });
            setItems(data);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, category, sort, mine, isMember]);
    useEffect(() => {
        const id = setTimeout(() => { load(); }, 250);
        return () => clearTimeout(id);
        // eslint-disable-next-line
    }, [search]);

    const totals = useMemo(() => {
        const inc = items.filter(i => i.type === "income").reduce((s, i) => s + parseFloat(i.amount), 0);
        const exp = items.filter(i => i.type === "expense").reduce((s, i) => s + parseFloat(i.amount), 0);
        return { inc, exp, count: items.length };
    }, [items]);

    const exportCsv = () => {
        const header = ["date","title","type","category","amount","payment_method","notes"];
        const rows = items.map(i => [i.occurred_at, i.title, i.type, i.category, i.amount, i.payment_method || "", (i.notes || "").replace(/\n/g, " ")]);
        const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `flowbudget-expenses-${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("CSV exported");
    };

    const onDelete = async () => {
        if (!deleteId) return;
        try {
            await api.delete(`/expenses/${deleteId}`);
            toast.success("Deleted");
            setItems(items.filter(i => i.id !== deleteId));
        } catch { toast.error("Failed to delete"); }
        finally { setDeleteId(null); }
    };

    return (
        <div className="space-y-4" data-testid="expenses-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Expenses</h1>
                    <p className="text-sm text-slate-500 mt-1">{totals.count} entries · Income {fmtCurrency(totals.inc, cur)} · Expenses {fmtCurrency(totals.exp, cur)}</p>
                </div>
                <div className="flex items-center gap-2">
                    {isMember ? (
                        <Tabs value={mine ? "mine" : "household"} onValueChange={v => setMine(v === "mine")}>
                            <TabsList className="bg-slate-100 rounded-xl p-1">
                                <TabsTrigger data-testid="scope-household" value="household" className="rounded-lg data-[state=active]:bg-white gap-1.5">
                                    <Users className="w-3.5 h-3.5" /> <span className="hidden sm:inline">All household</span>
                                </TabsTrigger>
                                <TabsTrigger data-testid="scope-mine" value="mine" className="rounded-lg data-[state=active]:bg-white gap-1.5">
                                    <UserIcon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Just me</span>
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                    ) : null}
                    <input ref={scanInputRef} type="file" accept="image/*" className="hidden" data-testid="expenses-scan-input"
                        onChange={e => onScanFile(e.target.files?.[0])} />
                    <Button variant="outline" onClick={onScanClick} disabled={scanning} data-testid="expenses-scan-btn" className="rounded-xl gap-1.5">
                        {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : canScan ? <ScanLine className="w-4 h-4" /> : <Lock className="w-4 h-4 text-amber-500" />}
                        <span className="hidden sm:inline">{scanning ? "Reading…" : "Scan"}</span>
                    </Button>
                    <Button variant="outline" onClick={exportCsv} data-testid="export-csv" className="rounded-xl gap-1.5">
                        <Download className="w-4 h-4" /> Export
                    </Button>
                    <Button onClick={() => setEditing({ _new: true, type: "expense", category: "Other", payment_method: "Credit Card", occurred_at: new Date().toISOString().slice(0,10), is_recurring: false })} data-testid="new-expense-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl gap-1.5">
                        <Plus className="w-4 h-4" /> Add
                    </Button>
                </div>
            </div>

            <div className="card-elev rounded-2xl p-2.5 lg:p-4 flex items-center gap-2 lg:gap-3 overflow-x-auto no-scrollbar lg:overflow-visible" data-testid="filters">
                <div className="relative flex-shrink-0 w-32 lg:flex-1 lg:w-auto lg:min-w-[200px]">
                    <Search className="w-3.5 h-3.5 lg:w-4 lg:h-4 absolute left-2.5 lg:left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input data-testid="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title or notes…" className="h-9 lg:h-11 rounded-xl pl-7 lg:pl-9 text-sm" />
                </div>
                <div className="flex items-center flex-nowrap lg:flex-wrap gap-2">
                    <Select value={type} onValueChange={setType}>
                        <SelectTrigger data-testid="type-filter" className="h-9 lg:h-11 rounded-xl w-[5.5rem] lg:w-36 px-2 lg:px-3 text-xs lg:text-sm flex-shrink-0"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All types</SelectItem>
                            <SelectItem value="expense">Expenses</SelectItem>
                            <SelectItem value="income">Income</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger data-testid="cat-filter" className="h-9 lg:h-11 rounded-xl w-28 lg:w-44 px-2 lg:px-3 text-xs lg:text-sm flex-shrink-0"><SelectValue placeholder="All categories" /></SelectTrigger>
                        <SelectContent className="max-h-80">
                            <SelectItem value="all">All categories</SelectItem>
                            {categoryNames.map(c => {
                                const { icon: CatIcon, color } = iconForCategoryName(categories, c);
                                return <SelectItem key={c} value={c}><span className="inline-flex items-center gap-2"><CatIcon className="w-3.5 h-3.5" style={{ color }} />{c}</span></SelectItem>;
                            })}
                        </SelectContent>
                    </Select>
                    <Select value={sort} onValueChange={setSort}>
                        <SelectTrigger data-testid="sort-filter" className="h-9 lg:h-11 rounded-xl w-28 lg:w-44 px-2 lg:px-3 text-xs lg:text-sm flex-shrink-0"><SelectValue /></SelectTrigger>
                        <SelectContent>{SORTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                    {/* Table/cards toggle only matters at lg+ — mobile always uses the compact stacked list below */}
                    <Tabs value={view} onValueChange={setView} className="hidden lg:block">
                        <TabsList className="bg-slate-100 rounded-xl p-1 flex-shrink-0">
                            <TabsTrigger data-testid="view-table" value="table" className="rounded-lg data-[state=active]:bg-white">
                                <TableIcon className="w-4 h-4" />
                            </TabsTrigger>
                            <TabsTrigger data-testid="view-cards" value="cards" className="rounded-lg data-[state=active]:bg-white">
                                <LayoutGrid className="w-4 h-4" />
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="card-elev rounded-2xl p-10 text-center text-slate-400 text-sm">Loading…</div>
            ) : items.length === 0 ? (
                <div className="card-elev rounded-2xl">
                    <EmptyState
                        icon={Receipt}
                        title="No expenses yet"
                        description="Track your first expense to start seeing insights."
                        action={
                            <Button onClick={() => setEditing({ _new: true, type: "expense", category: "Other", occurred_at: new Date().toISOString().slice(0,10), is_recurring: false })} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl">
                                <Plus className="w-4 h-4 mr-1.5" /> Add expense
                            </Button>
                        }
                    />
                </div>
            ) : (
                <>
                    {/* Mobile: compact stacked list, no horizontal scroll — always shown below lg regardless of the table/cards toggle */}
                    <div className="lg:hidden card-elev rounded-2xl divide-y divide-slate-100 overflow-hidden" data-testid="expenses-mobile-list">
                        {items.map(i => {
                            const isIncome = i.type === "income";
                            const { icon: CatIcon, color } = iconForCategoryName(categories, i.category);
                            return (
                                <div key={i.id} className="flex items-center gap-2.5 px-3 py-2.5">
                                    <span className={`w-8 h-8 rounded-lg grid place-items-center flex-shrink-0 ${isIncome ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                                        {isIncome ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold text-sm text-slate-900 truncate flex items-center gap-1.5">
                                            {i.title}
                                            {isMember && !mine && i.user_id !== user.id ? (
                                                <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600 flex-shrink-0">{memberName(i.user_id)}</span>
                                            ) : null}
                                        </div>
                                        <div className="flex items-center gap-1 text-[11px] text-slate-500 mt-0.5">
                                            <CatIcon className="w-3 h-3 flex-shrink-0" style={{ color }} />
                                            <span className="truncate">{i.category}</span>
                                            <span className="flex-shrink-0">· {fmtDate(i.occurred_at)}</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end flex-shrink-0 gap-0.5">
                                        <span className={`font-display font-bold text-sm tabular-nums ${isIncome ? "text-emerald-600" : "text-slate-900"}`}>
                                            {isIncome ? "+" : "−"}{fmtCurrency(i.amount, cur)}
                                        </span>
                                        <div className="flex gap-0.5">
                                            <button onClick={() => setEditing({ ...i, occurred_at: i.occurred_at?.slice(0,10) })} data-testid={`m-edit-${i.id}`} className="p-1 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => setDeleteId(i.id)} data-testid={`m-delete-${i.id}`} className="p-1 rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Desktop (lg+): unchanged table/cards toggle */}
                    <div className="hidden lg:block">
                    {view === "table" ? (
                <div className="card-elev rounded-2xl overflow-hidden" data-testid="expenses-table">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50/70 text-slate-500 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="text-left font-semibold px-5 py-3">Title</th>
                                    <th className="text-left font-semibold px-5 py-3">Category</th>
                                    <th className="text-left font-semibold px-5 py-3 hidden md:table-cell">Payment</th>
                                    <th className="text-left font-semibold px-5 py-3 hidden md:table-cell">Date</th>
                                    <th className="text-right font-semibold px-5 py-3">Amount</th>
                                    <th className="text-right font-semibold px-5 py-3 w-28"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map(i => {
                                    const isIncome = i.type === "income";
                                    return (
                                        <tr key={i.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-5 py-3.5">
                                                <div className="flex items-center gap-3">
                                                    <span className={`w-8 h-8 rounded-xl grid place-items-center ${isIncome ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                                                        {isIncome ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                                                    </span>
                                                    <div>
                                                        <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                                                            {i.title}
                                                            {isMember && !mine && i.user_id !== user.id ? (
                                                                <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600">{memberName(i.user_id)}</span>
                                                            ) : null}
                                                        </div>
                                                        {i.notes ? <div className="text-xs text-slate-500 truncate max-w-[220px]">{i.notes}</div> : null}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                {(() => {
                                                    const { icon: CatIcon, color } = iconForCategoryName(categories, i.category);
                                                    return (
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold" style={{ backgroundColor: `${color}18`, color }}>
                                                            <CatIcon className="w-3.5 h-3.5" />
                                                            {i.category}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-5 py-3.5 text-slate-600 hidden md:table-cell">{i.payment_method || "—"}</td>
                                            <td className="px-5 py-3.5 text-slate-600 hidden md:table-cell">{fmtDate(i.occurred_at)}</td>
                                            <td className={`px-5 py-3.5 text-right font-display font-bold tabular-nums ${isIncome ? "text-emerald-600" : "text-slate-900"}`}>
                                                {isIncome ? "+" : "−"}{fmtCurrency(i.amount, cur)}
                                            </td>
                                            <td className="px-5 py-3.5 text-right">
                                                <div className="inline-flex gap-1">
                                                    <button onClick={() => setEditing({ ...i, occurred_at: i.occurred_at?.slice(0,10) })} data-testid={`edit-${i.id}`} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                                                        <Pencil className="w-4 h-4" />
                                                    </button>
                                                    <button onClick={() => setDeleteId(i.id)} data-testid={`delete-${i.id}`} className="p-1.5 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600">
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="expenses-cards">
                    {items.map(i => {
                        const isIncome = i.type === "income";
                        return (
                            <div key={i.id} className="card-elev rounded-2xl p-5">
                                <div className="flex items-start justify-between gap-3 mb-2">
                                    <div className="flex items-center gap-2.5 min-w-0">
                                        <span className={`w-9 h-9 rounded-xl grid place-items-center ${isIncome ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                                            {isIncome ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                                        </span>
                                        <div className="min-w-0">
                                            <div className="font-semibold text-slate-900 truncate">{i.title}</div>
                                            <div className="text-xs text-slate-500 flex items-center gap-1.5">
                                                {fmtDate(i.occurred_at)}
                                                {isMember && !mine && i.user_id !== user.id ? (
                                                    <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600">{memberName(i.user_id)}</span>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`font-display font-bold tabular-nums ${isIncome ? "text-emerald-600" : "text-slate-900"}`}>{isIncome ? "+" : "−"}{fmtCurrency(i.amount, cur)}</div>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap text-xs">
                                    {(() => {
                                        const { icon: CatIcon, color } = iconForCategoryName(categories, i.category);
                                        return (
                                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md font-semibold" style={{ backgroundColor: `${color}18`, color }}>
                                                <CatIcon className="w-3.5 h-3.5" />
                                                {i.category}
                                            </span>
                                        );
                                    })()}
                                    {i.payment_method ? <span className="text-slate-500">{i.payment_method}</span> : null}
                                    {i.is_recurring ? <span className="px-2 py-0.5 rounded-md font-semibold bg-indigo-50 text-indigo-700">Recurring</span> : null}
                                </div>
                                {i.notes ? <p className="mt-3 text-sm text-slate-600 line-clamp-2">{i.notes}</p> : null}
                                <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end gap-1">
                                    <button onClick={() => setEditing({ ...i, occurred_at: i.occurred_at?.slice(0,10) })} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => setDeleteId(i.id)} className="p-1.5 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
                    )}
                    </div>
                </>
            )}

            <ExpenseEditor item={editing} onClose={() => setEditing(null)} onSaved={load} />

            <ScanReviewDialog scan={scanResult} onOpenChange={v => { if (!v) setScanResult(null); }} onSaved={load} />

            <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
                        <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={onDelete} data-testid="confirm-delete" className="bg-rose-600 hover:bg-rose-700 text-white">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function ExpenseEditor({ item, onClose, onSaved }) {
    const { categories, categoryNames } = useCategories();
    
    
    const [form, setForm] = useState(item || {});
    const [saving, setSaving] = useState(false);
    useEffect(() => { setForm(item || {}); }, [item]);
    if (!item) return null;

    const submit = async () => {
        if (!form.title || !form.amount) { toast.error("Title and amount are required"); return; }
        try {
            setSaving(true);
            const payload = {
                title: form.title, amount: parseFloat(form.amount), type: form.type || "expense",
                category: form.category || "Other", payment_method: form.payment_method,
                notes: form.notes || null, is_recurring: !!form.is_recurring,
                recurrence: form.is_recurring ? (form.recurrence || "monthly") : null,
                next_due_date: form.is_recurring ? (form.next_due_date || null) : null,
                occurred_at: new Date(form.occurred_at || new Date()).toISOString(),
            };
            if (form._new) await api.post("/expenses", payload);
            else await api.patch(`/expenses/${form.id}`, payload);
            toast.success(form._new ? "Created" : "Updated");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    const cats = categoryNames;

    return (
        <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="expense-editor" className="sm:max-w-lg rounded-2xl">
                <DialogHeader><DialogTitle className="font-display text-xl">{form._new ? "Add entry" : "Edit entry"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <FieldL label="Type">
                            <Select value={form.type || "expense"} onValueChange={v => setForm({...form, type: v, category: cats.includes(form.category) ? form.category : (v === "income" ? "Salary" : "Other") })}>
                                <SelectTrigger data-testid="ed-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="expense">Expense</SelectItem>
                                    <SelectItem value="income">Income</SelectItem>
                                </SelectContent>
                            </Select>
                        </FieldL>
                        <FieldL label="Date">
                            <Input data-testid="ed-date" type="date" value={form.occurred_at || ""} onChange={e => setForm({...form, occurred_at: e.target.value})} />
                        </FieldL>
                    </div>
                    <FieldL label="Title"><Input data-testid="ed-title" value={form.title || ""} onChange={e => setForm({...form, title: e.target.value})} /></FieldL>
                    <div className="grid grid-cols-2 gap-3">
                        <FieldL label="Amount"><Input data-testid="ed-amount" type="number" step="0.01" value={form.amount || ""} onChange={e => setForm({...form, amount: e.target.value})} /></FieldL>
                        <FieldL label="Category">
                            <Select value={form.category || "Other"} onValueChange={v => setForm({...form, category: v})}>
                                <SelectTrigger data-testid="ed-category"><SelectValue /></SelectTrigger>
                                <SelectContent>{cats.map(c => {
                                    const { icon: CatIcon, color } = iconForCategoryName(categories, c);
                                    return <SelectItem key={c} value={c}><span className="inline-flex items-center gap-2"><CatIcon className="w-3.5 h-3.5" style={{ color }} />{c}</span></SelectItem>;
                                })}</SelectContent>
                            </Select>
                        </FieldL>
                    </div>
                    <FieldL label="Payment method">
                        <Select value={form.payment_method || "Credit Card"} onValueChange={v => setForm({...form, payment_method: v})}>
                            <SelectTrigger data-testid="ed-payment"><SelectValue /></SelectTrigger>
                            <SelectContent>{PAYMENT_METHODS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                    </FieldL>
                    <FieldL label="Notes"><Textarea data-testid="ed-notes" rows={2} value={form.notes || ""} onChange={e => setForm({...form, notes: e.target.value})} /></FieldL>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input type="checkbox" checked={!!form.is_recurring} onChange={e => setForm({...form, is_recurring: e.target.checked})} data-testid="ed-recurring" /> Recurring
                    </label>
                    {form.is_recurring ? (
                        <div className="grid grid-cols-2 gap-3">
                            <FieldL label="Repeats">
                                <Select value={form.recurrence || "monthly"} onValueChange={v => setForm({...form, recurrence: v})}>
                                    <SelectTrigger data-testid="ed-recurrence"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="weekly">Weekly</SelectItem>
                                        <SelectItem value="monthly">Monthly</SelectItem>
                                        <SelectItem value="yearly">Yearly</SelectItem>
                                    </SelectContent>
                                </Select>
                            </FieldL>
                            <FieldL label="Next due date">
                                <Input data-testid="ed-next-due" type="date" value={form.next_due_date || ""} onChange={e => setForm({...form, next_due_date: e.target.value})} />
                            </FieldL>
                        </div>
                    ) : null}
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button data-testid="ed-save" disabled={saving} onClick={submit} className="bg-indigo-600 hover:bg-indigo-700 text-white">{form._new ? "Create" : "Save"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FieldL({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">{label}</Label>
            {children}
        </div>
    );
}
